import { useEffect, useState, useCallback } from "react";
import API from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Pulse as Activity, ArrowsClockwise, CheckCircle, Clock, WarningCircle, XCircle, Lightning } from "@phosphor-icons/react";
import { OpsAlertChannelCard } from "../components/OpsAlertChannelCard";

const STATUS_META = {
  healthy:       { label: "Healthy",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800", icon: CheckCircle },
  overdue:       { label: "Overdue",       cls: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800",         icon: Clock },
  stalled:       { label: "Stalled",       cls: "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800",                       icon: WarningCircle },
  errored:       { label: "Errored",       cls: "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800",                       icon: XCircle },
  no_telemetry:  { label: "No data yet",   cls: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700",             icon: Activity },
};

function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return "in " + relativeTime(new Date(Date.now() + 2 * Math.abs(ms)).toISOString()).replace(" ago", "");
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch {
    return "—";
  }
}

function nextFireText(iso) {
  if (!iso) return "—";
  try {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms < 0) return "expected now";
    const s = Math.floor(ms / 1000);
    if (s < 60) return `in ${s}s`;
    if (s < 3600) return `in ${Math.floor(s / 60)}m`;
    if (s < 86400) return `in ${Math.floor(s / 3600)}h`;
    return `in ${Math.floor(s / 86400)}d`;
  } catch {
    return "—";
  }
}

export default function SchedulerHealthPage() {
  const [data, setData] = useState({ summary: {}, schedulers: [] });
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [running, setRunning] = useState({}); // task → boolean

  const fetchHealth = useCallback(async () => {
    try {
      const res = await API.get("/platform/scheduler-health");
      setData(res.data);
    } catch (err) {
      console.error("scheduler-health fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const runNow = useCallback(async (task, url, label) => {
    if (!url) return;
    setRunning((m) => {
      if (m[task]) return m;
      return { ...m, [task]: true };
    });
    const tid = toast.loading(`Running ${label}…`);
    try {
      const res = await API.post(url);
      const count = res?.data?.sent ?? res?.data?.fired ?? res?.data?.updated ?? 0;
      toast.success(`${label} fired — ${count} item${count === 1 ? "" : "s"} processed`, { id: tid });
      setTimeout(fetchHealth, 800);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      toast.error(`${label} failed: ${detail}`, { id: tid });
    } finally {
      setRunning((m) => ({ ...m, [task]: false }));
    }
  }, [fetchHealth]);

  useEffect(() => {
    fetchHealth();
    if (!autoRefresh) return undefined;
    const id = setInterval(fetchHealth, 15_000);
    return () => clearInterval(id);
  }, [fetchHealth, autoRefresh]);

  const grouped = (data.schedulers || []).reduce((acc, row) => {
    (acc[row.category] ||= []).push(row);
    return acc;
  }, {});
  const categories = Object.keys(grouped).sort();

  const summaryTiles = [
    { label: "Schedulers",   value: data.summary.total ?? 0,        cls: "text-slate-700 dark:text-slate-300" },
    { label: "Healthy",      value: data.summary.healthy ?? 0,      cls: "text-emerald-600 dark:text-emerald-400" },
    { label: "Overdue",      value: data.summary.overdue ?? 0,      cls: "text-amber-600 dark:text-amber-400" },
    { label: "Stalled",      value: data.summary.stalled ?? 0,      cls: "text-red-600 dark:text-red-400" },
    { label: "Errored",      value: data.summary.errored ?? 0,      cls: "text-red-600 dark:text-red-400" },
    { label: "No telemetry", value: data.summary.no_telemetry ?? 0, cls: "text-slate-500 dark:text-slate-400" },
  ];

  return (
    <div className="space-y-6" data-testid="scheduler-health-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Scheduler Health</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live status of every background job — last fired, expected next fire, error trail.
            Auto-refreshes every 15s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(v => !v)}
            data-testid="scheduler-health-toggle-autorefresh"
          >
            <Activity className="w-4 h-4 mr-1.5" weight={autoRefresh ? "fill" : "regular"} />
            Auto-refresh: {autoRefresh ? "On" : "Off"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchHealth}
            disabled={loading}
            data-testid="scheduler-health-refresh"
          >
            <ArrowsClockwise className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {summaryTiles.map((t) => (
          <Card key={t.label} className="border-slate-200 dark:border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{t.label}</div>
              <div className={`text-3xl font-bold mt-1 ${t.cls}`}>{t.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Slack / Teams alert channel */}
      <OpsAlertChannelCard />

      {/* Category groups */}
      {categories.map((cat) => (
        <Card key={cat} data-testid={`scheduler-health-category-${cat}`}>
          <CardHeader>
            <CardTitle className="text-base">{cat}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Scheduler</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Last fired</th>
                    <th className="py-2 pr-4 font-medium">Next expected</th>
                    <th className="py-2 pr-4 font-medium text-right">Total ticks</th>
                    <th className="py-2 pr-4 font-medium text-right">Items</th>
                    <th className="py-2 pr-4 font-medium text-right">Run now</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[cat].map((row) => {
                    const meta = STATUS_META[row.status] || STATUS_META.no_telemetry;
                    const Icon = meta.icon;
                    return (
                      <tr key={row.task} className="border-b last:border-0 hover:bg-muted/30" data-testid={`scheduler-row-${row.task}`}>
                        <td className="py-3 pr-4">
                          <div className="font-medium">{row.label}</div>
                          <div className="text-xs text-muted-foreground font-mono">{row.task}</div>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge variant="outline" className={meta.cls}>
                            <Icon className="w-3.5 h-3.5 mr-1" weight="fill" />
                            {meta.label}
                          </Badge>
                          {row.last_error && (
                            <div className="text-xs text-red-600 dark:text-red-400 mt-1 max-w-md truncate" title={row.last_error}>
                              {row.last_error}
                            </div>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-muted-foreground">{relativeTime(row.last_check)}</td>
                        <td className="py-3 pr-4 text-muted-foreground">{nextFireText(row.next_fire)}</td>
                        <td className="py-3 pr-4 text-right tabular-nums">{row.total_ticks ?? 0}</td>
                        <td className="py-3 pr-4 text-right tabular-nums">{row.total_count ?? 0}</td>
                        <td className="py-3 pr-4 text-right">
                          {row.run_now_url ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!!running[row.task]}
                              onClick={() => runNow(row.task, row.run_now_url, row.label)}
                              data-testid={`scheduler-run-now-${row.task}`}
                              className="h-7 px-2.5"
                            >
                              <Lightning className={`w-3.5 h-3.5 mr-1 ${running[row.task] ? "animate-pulse" : ""}`} weight={running[row.task] ? "fill" : "regular"} />
                              {running[row.task] ? "Running…" : "Run now"}
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground/60" title="No run-now endpoint exposed for this scheduler yet — runs on its natural interval only.">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      {!loading && (data.schedulers || []).length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground" data-testid="scheduler-health-empty">
            No scheduler data available. Backend may be still booting.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
